// ============================================================================
// GESTIMMONY — Révision IRL automatique
// ============================================================================
// - Récupère les indices INSEE (metropole/dom/corse) publiés
// - Calcule le nouveau loyer selon la formule légale
// - Prévisualise + génère la lettre PDF de notification (via /letters)
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { api, fmtEUR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { TrendingUp, Calculator, ArrowRight, Download, FileText } from "lucide-react";

const ZONES = [
  { value: "metropole", label: "Métropole" },
  { value: "dom", label: "DOM (Guadeloupe, Martinique…)" },
  { value: "corse", label: "Corse" },
];

export default function IRLRevisionPage() {
  const [zone, setZone] = useState("metropole");
  const [indices, setIndices] = useState([]);
  const [latest, setLatest] = useState("");
  const [rent, setRent] = useState("");
  const [oldQuarter, setOldQuarter] = useState("");
  const [newQuarter, setNewQuarter] = useState("");
  const [result, setResult] = useState(null);
  const [computing, setComputing] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [revisionDate, setRevisionDate] = useState(new Date().toISOString().slice(0, 10));
  const [downloading, setDownloading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [leases, setLeases] = useState([]);
  const [pickTenant, setPickTenant] = useState("");
  const [pickProperty, setPickProperty] = useState("");

  useEffect(() => {
    api.get("/tenants").then(({ data }) => setTenants(data)).catch(() => {});
    api.get("/properties").then(({ data }) => setProperties(data)).catch(() => {});
    api.get("/leases").then(({ data }) => setLeases(data)).catch(() => {});
  }, []);

  // Sélection d'un locataire → pré-remplit le nom + déduit le bien/loyer/zone depuis son bail
  const onPickTenant = (id) => {
    setPickTenant(id);
    const t = tenants.find((x) => x.id === id);
    if (t) setTenantName(`${t.first_name} ${t.last_name}`.trim());
    const lease = leases.find((l) => l.tenant_id === id);
    if (lease) {
      if (lease.rent_amount != null) setRent(String(lease.rent_amount));
      if (lease.property_id) onPickProperty(lease.property_id);
    }
  };

  // Sélection d'un bien → pré-remplit l'adresse + auto-détecte la zone IRL
  const onPickProperty = async (id) => {
    setPickProperty(id);
    const p = properties.find((x) => x.id === id);
    if (!p) return;
    setPropertyAddress([p.address, p.postal_code, p.city].filter(Boolean).join(" "));
    if (p.postal_code) {
      try {
        const { data } = await api.get(`/territoires/zone?postal_code=${encodeURIComponent(p.postal_code)}`);
        if (data.zone && data.zone !== zone) {
          setZone(data.zone);
          toast.info(`Zone IRL détectée : ${ZONES.find((z) => z.value === data.zone)?.label || data.zone}`);
        }
      } catch { /* silencieux */ }
    }
  };

  useEffect(() => {
    api.get(`/irl/indices?zone=${zone}`).then(({ data }) => {
      setIndices(data.indices);
      setLatest(data.latest);
      if (data.indices.length >= 5 && !newQuarter) {
        setNewQuarter(data.latest);
        // suggest same quarter previous year
        const [y, t] = data.latest.split("-");
        const prev = `${parseInt(y) - 1}-${t}`;
        if (data.indices.find((i) => i.quarter === prev)) setOldQuarter(prev);
      }
    });
    // eslint-disable-next-line
  }, [zone]);

  const compute = async () => {
    if (!rent || !oldQuarter || !newQuarter) {
      toast.error("Remplissez tous les champs de calcul");
      return;
    }
    setComputing(true);
    try {
      const { data } = await api.post("/irl/compute", {
        current_rent: parseFloat(rent),
        old_quarter: oldQuarter,
        new_quarter: newQuarter,
        zone,
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setResult(null);
    } finally { setComputing(false); }
  };

  const downloadLetter = async () => {
    if (!result) return;
    if (!tenantName || !propertyAddress) {
      toast.error("Renseignez le locataire et l'adresse du logement");
      return;
    }
    setDownloading(true);
    try {
      const values = {
        tenant_name: tenantName,
        property_address: propertyAddress,
        old_rent: result.old_rent,
        new_rent: result.new_rent,
        old_index_label: result.old_quarter,
        old_index_value: result.old_index,
        new_index_label: result.new_quarter,
        new_index_value: result.new_index,
        revision_date: revisionDate,
      };
      const res = await api.post("/letters/loyer_revision_irl/pdf", { values },
                                 { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gestimmony-revision-irl-${tenantName.toLowerCase().replaceAll(" ", "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Courrier de révision généré");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setDownloading(false); }
  };

  const trendPositive = result && result.delta_eur >= 0;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="irl-page">
      <div>
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold bg-teal-100 text-teal-800 px-2 py-1 rounded-full mb-2">
          <TrendingUp className="h-3 w-3" /> Indices INSEE à jour
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
          Révision IRL automatique
        </h1>
        <p className="mt-1 text-xs font-medium text-teal-700">
          IRL = Indice de Référence des Loyers (publié chaque trimestre par l'INSEE)
        </p>
        <p className="mt-1 text-slate-600 text-sm">
          Calcul instantané du nouveau loyer selon la formule légale et génération de la lettre recommandée en un clic.
        </p>
      </div>

      {/* Sélection auto-remplissage locataire + bien (visible dès l'arrivée sur la page) */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-teal-700" />
          <h2 className="font-heading font-bold">Locataire et bien concernés</h2>
          <span className="text-xs text-slate-500 ml-2">Pré-remplissage automatique</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase text-slate-600">Locataire</Label>
            <Select value={pickTenant} onValueChange={onPickTenant}>
              <SelectTrigger className="mt-1" data-testid="irl-tenant-select">
                <SelectValue placeholder="Choisir un locataire…" />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`irl-tenant-option-${t.id}`}>
                    {t.first_name} {t.last_name}
                  </SelectItem>
                ))}
                {tenants.length === 0 && (
                  <SelectItem value="_none" disabled>Aucun locataire enregistré</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-600">Bien concerné</Label>
            <Select value={pickProperty} onValueChange={onPickProperty}>
              <SelectTrigger className="mt-1" data-testid="irl-property-select">
                <SelectValue placeholder="Choisir un bien…" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`irl-property-option-${p.id}`}>
                    {p.label} — {p.city}
                  </SelectItem>
                ))}
                {properties.length === 0 && (
                  <SelectItem value="_none" disabled>Aucun bien enregistré</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Calculatrice */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calculator className="h-5 w-5 text-teal-700" />
          <h2 className="font-heading font-bold">Calculatrice de révision</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase text-slate-600">Zone géographique</Label>
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger className="mt-1" data-testid="irl-zone-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ZONES.map((z) => <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-600">Loyer actuel HC (€)</Label>
            <Input
              data-testid="irl-rent-input"
              type="number"
              step="0.01"
              value={rent}
              onChange={(e) => setRent(e.target.value)}
              placeholder="820"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-600">IRL de référence (bail)</Label>
            <Select value={oldQuarter} onValueChange={setOldQuarter}>
              <SelectTrigger className="mt-1" data-testid="irl-old-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>
                {indices.map((i) => (
                  <SelectItem key={i.quarter} value={i.quarter}>
                    {i.quarter} — {i.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-600">Nouvel IRL</Label>
            <Select value={newQuarter} onValueChange={setNewQuarter}>
              <SelectTrigger className="mt-1" data-testid="irl-new-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>
                {indices.map((i) => (
                  <SelectItem key={i.quarter} value={i.quarter}>
                    {i.quarter} — {i.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            Dernier IRL publié pour cette zone : <b>{latest}</b>
          </div>
          <Button data-testid="irl-compute-btn" onClick={compute} disabled={computing} className="bg-[hsl(var(--primary))]">
            <Calculator className="h-4 w-4 mr-2" />
            {computing ? "Calcul…" : "Calculer"}
          </Button>
        </div>
      </Card>

      {/* Résultat */}
      {result && (
        <Card className="p-6 bg-gradient-to-br from-teal-50 to-emerald-50 border-teal-200" data-testid="irl-result">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center" data-testid="result-card">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Ancien loyer</div>
              <div className="mt-1 font-heading text-3xl font-bold text-slate-900">{fmtEUR(result.old_rent)}</div>
              <div className="text-xs text-slate-500 mt-1">IRL {result.old_quarter} · {result.old_index}</div>
            </div>
            <div className="text-center">
              <ArrowRight className="h-6 w-6 text-teal-700 mx-auto" />
              <div className={`mt-2 inline-block px-3 py-1 rounded-full text-xs font-bold ${
                trendPositive ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"
              }`}>
                {trendPositive ? "+" : ""}{result.delta_pct}% ({trendPositive ? "+" : ""}{fmtEUR(result.delta_eur)})
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">Nouveau loyer</div>
              <div className="mt-1 font-heading text-3xl font-bold text-teal-700" data-testid="irl-new-rent">
                {fmtEUR(result.new_rent)}
              </div>
              <div className="text-xs text-slate-500 mt-1">IRL {result.new_quarter} · {result.new_index}</div>
            </div>
          </div>
          <div className="mt-4 text-xs text-slate-600 italic border-t border-teal-200 pt-3">
            Formule : {result.old_rent} × ({result.new_index} / {result.old_index}) = <b>{fmtEUR(result.new_rent)}</b>
          </div>
        </Card>
      )}

      {/* Génération courrier */}
      {result && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-teal-700" />
            <h2 className="font-heading font-bold">Générer la lettre de notification</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase text-slate-600">Locataire</Label>
              <Select value={pickTenant} onValueChange={onPickTenant}>
                <SelectTrigger className="mt-1" data-testid="irl-tenant-select">
                  <SelectValue placeholder="Choisir un locataire…" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`irl-tenant-option-${t.id}`}>
                      {t.first_name} {t.last_name}
                    </SelectItem>
                  ))}
                  {tenants.length === 0 && (
                    <SelectItem value="_none" disabled>Aucun locataire enregistré</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-600">Bien concerné</Label>
              <Select value={pickProperty} onValueChange={onPickProperty}>
                <SelectTrigger className="mt-1" data-testid="irl-property-select">
                  <SelectValue placeholder="Choisir un bien…" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`irl-property-option-${p.id}`}>
                      {p.label} — {p.city}
                    </SelectItem>
                  ))}
                  {properties.length === 0 && (
                    <SelectItem value="_none" disabled>Aucun bien enregistré</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-600">Date d'application</Label>
              <Input data-testid="irl-date-input" type="date" value={revisionDate} onChange={(e) => setRevisionDate(e.target.value)} className="mt-1" />
            </div>
          </div>
          {/* Champs pré-remplis modifiables manuellement */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <div>
              <Label className="text-xs uppercase text-slate-500">Nom du locataire (modifiable)</Label>
              <Input data-testid="irl-tenant-input" value={tenantName} onChange={(e) => setTenantName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-500">Adresse du logement (modifiable)</Label>
              <Input data-testid="irl-property-input" value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button data-testid="irl-download-btn" onClick={downloadLetter} disabled={downloading} className="bg-[hsl(var(--primary))]">
              <Download className="h-4 w-4 mr-2" />
              {downloading ? "Génération…" : "Télécharger le courrier PDF"}
            </Button>
          </div>
        </Card>
      )}

      {/* Tableau indices */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold text-slate-900">Indices IRL publiés — {ZONES.find((z) => z.value === zone).label}</h2>
          <Badge variant="outline">{indices.length} trimestres</Badge>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {indices.map((i) => (
            <div key={i.quarter} className="border border-slate-200 rounded-lg px-3 py-2 text-center">
              <div className="text-[10px] uppercase text-slate-500 font-semibold">{i.quarter}</div>
              <div className="font-heading font-bold text-slate-900">{i.value}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
